import { getOrganizationServiceDays } from "../vend/serviceScheduleDays";
import { SessionUnavailableError } from "@vendistri/ui";
import { organizationType, userType } from "./types";
import {
  buildUrl,
  defaultFetchHeaders,
  fetchJson,
  getCsrfToken,
  normalizeIdentityEmail,
} from "../utils";
import { errorMessageFromResponse, jsonFromResponse } from "../apiError";
import { fetchBrowserSession } from "../vend/state/demoFlag";
import type { DirectDepositPlan } from "./subscription";

export const url = `${process.env.REACT_APP_BACKEND_URL}/user`;

export async function setupBusiness(name: string, timezone: string, signal?: AbortSignal) {
  return fetchJson<{ id: string; title: string }>(`${url}/organizations/business`, {
    method: "POST", credentials: "include", headers: { ...await defaultFetchHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ organizationTitle: name.trim(), organizationTimezone: timezone }), signal,
  }, "Could not set up your business.");
}

export type OrganizationUpgradePreview = {
  accountMode: "lead_only" | "full";
  trialEligible: boolean;
  trialDays: number;
  subscriptionPriceCents: number;
  freeMachineLimit?: number;
  includedMachineLimit: number;
  additionalMachineFeeCents: number;
  currency: string;
  paymentMethodRequired: boolean;
  activationDueNow: boolean;
  marketplaceAllowanceBenefit?: { label: string; quantity: number } | null;
};

export async function fetchOrganizationUpgradePreview(signal?: AbortSignal) {
  return fetchJson<OrganizationUpgradePreview>(`${url}/organization/upgrade-preview`, {
    credentials: "include", headers: await defaultFetchHeaders(), signal,
  }, "Could not load subscription options.");
}

export async function upgradeOrganization(signal?: AbortSignal, trialOffer?: import("../vend/api/trialOffers").TrialRedemption) {
  return fetchJson<OrganizationUpgradePreview>(`${url}/organization/upgrade`, {
    method: "POST", credentials: "include", headers: await defaultFetchHeaders(), signal,
    ...(trialOffer ? { body: JSON.stringify(trialOffer) } : {}),
  }, "Could not upgrade this organization.");
}

export type CommissionBankAccount = {
  id: string;
  label: string;
  bankName?: string;
  active?: boolean;
  ready?: boolean;
};

export type CommissionBankAccountsResponse = {
  monthlyPlan?: DirectDepositPlan;
  setupEnabled: boolean;
  profiles?: {
    id: string;
    label: string;
    ready: boolean;
    defaultAccountId: string | null;
    accounts: CommissionBankAccount[];
  }[];
  accounts?: CommissionBankAccount[];
  defaultAccountId?: string | null;
};

export async function commissionBankAccountsRequest<T = CommissionBankAccountsResponse>(
  kind: "recipient" | "organization",
  suffix = "",
  body?: unknown,
  signal?: AbortSignal,
  administrationOrganizationId?: string,
): Promise<T> {
  if (administrationOrganizationId && (kind !== "organization" || suffix || body !== undefined)) {
    throw new Error("Manage organization bank accounts through financial support.");
  }
  const path = administrationOrganizationId
    ? `/administration/organizations/${encodeURIComponent(administrationOrganizationId)}/commission-funding`
    : kind === "recipient" ? "/user/payout-accounts" : "/organization/commission-funding";
  return fetchJson<T>(`${process.env.REACT_APP_BACKEND_URL || ""}${path}${suffix}`, {
    method: body === undefined ? "GET" : "POST",
    credentials: "include",
    headers: await defaultFetchHeaders(),
    signal,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }, "Unable to update bank accounts");
}

export async function readBrowserSessionIdentity(signal: AbortSignal): Promise<string | null> {
  let response: Response;
  try {
    response = await fetchBrowserSession(url, { credentials: "include", cache: "no-store", signal });
  } catch (error) {
    if (signal.aborted) throw error;
    throw new SessionUnavailableError();
  }
  if (response.status >= 500) throw new SessionUnavailableError();
  if (response.status === 401) return null;
  if (!response.ok) throw new Error("Unable to verify session");
  const user = await response.json();
  if (typeof user?.id !== "string" || !user.id) throw new Error("Invalid session response");
  return user.id;
}

let csrfTokenRequest: Promise<string> | null = null;

const normalizeInviteStatus = (value: unknown): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const token = (raw.includes(".") ? raw.split(".").pop() || raw : raw)
    .trim()
    .toLowerCase();
  return token;
};

export const normalizeUserResponse = (user: any): userType => ({
  id: user.id,
  email: user.email,
  firstName: user.first_name ?? user.firstName ?? "",
  lastName: user.last_name ?? user.lastName ?? "",
  photoUrl: user.photo_url ?? user.photoUrl ?? null,
  phone: user.phone ?? "",
  sidebarRestoreLastVisited:
    user.sidebarRestoreLastVisited ?? user.sidebar_restore_last_visited ?? false,
  sidebarShowRememberedShortcuts:
    user.sidebarShowRememberedShortcuts ?? user.sidebar_show_remembered_shortcuts ?? true,
  machines: user.machines ?? [],
  locations: user.locations ?? [],
  isAdmin: user.isAdmin ?? user.is_admin ?? false,
  isOwner: user.isOwner ?? user.is_owner ?? false,
  isStaff: user.isStaff ?? user.is_staff ?? false,
  isContact: user.isContact ?? user.is_contact ?? false,
  isSuperuser: user.isSuperuser ?? user.is_superuser ?? false,
  isOperator: user.isOperator ?? user.is_operator ?? false,
  isAuthenticated: user.isAuthenticated ?? user.is_authenticated ?? false,
  created_at: user.created_at ?? user.createdAt,
  updated_at: user.updated_at ?? user.updatedAt,
});

export async function getUser(): Promise<userType> {
  const user = await fetchJson<any>(
    buildUrl(url, {}),
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to fetch user"
  );
  return normalizeUserResponse(user);
}

const normalizeOrganization = (org: any): organizationType => ({
  growthSettings: org.growthSettings,
  id: org.id,
  title: org.title ?? "",
  timezone: org.timezone ?? "UTC",
  notes: org.notes ?? "",
  accountMode: org.account_mode ?? org.accountMode ?? "full",
  logoUrl: org.logo_url ?? org.logoUrl ?? null,
  useDefaultLogo:
    org.use_default_logo ?? org.useDefaultLogo ?? true,
  billingSendToAdmins:
    org.billing_send_to_admins ?? org.billingSendToAdmins ?? false,
  taskPlanEmailsEnabled:
    org.task_plan_emails_enabled ?? org.taskPlanEmailsEnabled ?? true,
  dailySummaryEmailsEnabled:
    org.daily_summary_emails_enabled ?? org.dailySummaryEmailsEnabled ?? true,
  weeklyMetricsEmailsEnabled:
    org.weekly_metrics_emails_enabled ?? org.weeklyMetricsEmailsEnabled ?? true,
  marketplaceOwnerMatchEmailsEnabled:
    org.marketplace_owner_match_emails_enabled ?? org.marketplaceOwnerMatchEmailsEnabled ?? true,
  marketplaceAdminMatchEmailsEnabled:
    org.marketplace_admin_match_emails_enabled ?? org.marketplaceAdminMatchEmailsEnabled ?? true,
  warehouseTaskFlowEnabled:
    org.warehouse_task_flow_enabled ??
    org.warehouseTaskFlowEnabled ??
    org.use_vendistri_inventory ??
    org.useVendistriInventory ??
    false,
  operatorTaskClaimingEnabled:
    org.operator_task_claiming_enabled ??
    org.operatorTaskClaimingEnabled ??
    false,
  executionSessionsEnabled: org.execution_sessions_enabled ?? org.executionSessionsEnabled ?? true,
  executionLocationIngestionEnabled: org.execution_location_ingestion_enabled ?? org.executionLocationIngestionEnabled ?? true,
  executionLiveWebEnabled: org.execution_live_web_enabled ?? org.executionLiveWebEnabled ?? true,
  executionBackgroundLocationEnabled: org.execution_background_location_enabled ?? org.executionBackgroundLocationEnabled ?? true,
  executionHistoryEnabled: org.execution_history_enabled ?? org.executionHistoryEnabled ?? true,
  serviceScheduleDays:
    org.service_schedule_days ??
    org.serviceScheduleDays ??
    getOrganizationServiceDays(),
  defaultServiceFrequency:
    org.default_service_frequency ?? org.defaultServiceFrequency ?? 7,
  maxSuggestedRouteDriveMinutes:
    org.max_suggested_route_drive_minutes ??
    org.maxSuggestedRouteDriveMinutes ??
    90,
  maxSuggestedRouteDistanceMiles:
    org.max_suggested_route_distance_miles ??
    org.maxSuggestedRouteDistanceMiles ??
    20,
  maxSuggestedRouteDayLocations:
    org.max_suggested_route_day_locations ??
    org.maxSuggestedRouteDayLocations ??
    18,
  maxSuggestedRouteDayMachines:
    org.max_suggested_route_day_machines ??
    org.maxSuggestedRouteDayMachines ??
    30,
});

type OrganizationLogoPayload = Pick<
  organizationType,
  "logoUrl"
>;

const normalizeOrganizationLogo = (payload: any): OrganizationLogoPayload => {
  if (typeof payload === "string" || payload === null) {
    return { logoUrl: payload };
  }
  return { logoUrl: payload?.logo_url ?? payload?.logoUrl ?? null };
};

export async function fetchOrganization(): Promise<organizationType> {
  const org = await fetchJson<any>(
    buildUrl(`${url}/organization`, {}),
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to fetch organization"
  );
  return normalizeOrganization(org);
}

export async function uploadOrganizationLogo(
  file: File
): Promise<OrganizationLogoPayload> {
  const form = new FormData();
  form.append("file", file);
  const token = getCsrfToken() || (await setCsrfToken());
  const res = await fetch(`${url}/organization/logo`, {
    method: "POST",
    credentials: "include",
    headers: {
      // Let the browser generate multipart/form-data with its boundary.
      ...(await defaultFetchHeaders(null)),
      "X-CSRFToken": token,
    },
    body: form,
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to upload logo")
    );
  }
  return normalizeOrganizationLogo(
    await jsonFromResponse(res, "Failed to upload logo")
  );
}

export async function removeOrganizationLogo(): Promise<void> {
  const token = getCsrfToken() || (await setCsrfToken());
  const res = await fetch(`${url}/organization/logo/remove`, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "X-CSRFToken": token,
    },
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to remove logo")
    );
  }
}

export async function setDefaultOrganizationLogo(): Promise<void> {
  const token = getCsrfToken() || (await setCsrfToken());
  const res = await fetch(`${url}/organization/logo/default`, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "X-CSRFToken": token,
    },
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to set default logo")
    );
  }
}

export async function updateOrganization(
  payload: {
    title?: string;
    timezone?: string;
    notes?: string;
    billingSendToAdmins?: boolean;
    taskPlanEmailsEnabled?: boolean;
    dailySummaryEmailsEnabled?: boolean;
    weeklyMetricsEmailsEnabled?: boolean;
    marketplaceOwnerMatchEmailsEnabled?: boolean;
    marketplaceAdminMatchEmailsEnabled?: boolean;
    warehouseTaskFlowEnabled?: boolean;
    operatorTaskClaimingEnabled?: boolean;
    serviceScheduleDays?: string[];
    defaultLocationLeadAssigneeId?: string | null;
    applyGrowthTaskAssigneeTo?: import("../vend/types").DefaultAssigneeApplyScope;
    expectedGrowthSettingsRevision?: number;
    defaultCommissionFundingAccountId?: string;
    defaultServiceFrequency?: number;
    maxSuggestedRouteDriveMinutes?: number;
    maxSuggestedRouteDistanceMiles?: number;
    maxSuggestedRouteDayLocations?: number;
    maxSuggestedRouteDayMachines?: number;
    locationCadenceChanges?: Array<{
      locationId: string;
      expected: { frequency?: number | null; dayOfWeek?: string | null };
      next: { frequency: number; dayOfWeek: string };
    }>;
  }
): Promise<organizationType> {
  const res = await fetch(`${url}/organization/edit`, {
    method: "POST",
    credentials: "include",
    headers : await defaultFetchHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to update organization")
    );
  }
  const org = await jsonFromResponse(res, "Failed to update organization");
  return normalizeOrganization(org);
}

export function setCsrfToken(): Promise<string> {
  if (csrfTokenRequest) return csrfTokenRequest;

  csrfTokenRequest = (async () => {
    const res = await fetch(`${url}/csrf`, {
      method: "GET",
      credentials: "include",
    });
    if (!res.ok) {
      throw new Error(
        await errorMessageFromResponse(res, "Could not prepare sign in. Please try again.")
      );
    }
    const payload = await jsonFromResponse<{ csrftoken?: string }>(
      res,
      "Could not prepare sign in. Please try again."
    );
    const token = payload.csrftoken;
    if (!token) {
      throw new Error("Could not prepare sign in. Please try again.");
    }
    try {
      window.localStorage.setItem("csrftoken", token);
    } catch (error) {
      console.error(error);
    }
    return token;
  })();

  csrfTokenRequest.finally(() => {
    csrfTokenRequest = null;
  }).catch(() => {});
  return csrfTokenRequest;
}

export async function activateContactAccount(params: {
  uid: string;
  token: string;
  password: string;
}): Promise<{ status: string }> {
  const res = await fetch(`${url}/contact/activate`, {
    method: "POST",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  return await jsonFromResponse<{ status: string }>(
    res,
    "Failed to activate account"
  );
}

export async function getContactInviteMeta(uid: string): Promise<{
  firstName?: string;
  lastName?: string;
  email?: string;
}> {
  const res = await fetch(
    `${url}/contact/meta?uid=${encodeURIComponent(uid)}`,
    {
      method: "GET",
      headers : await defaultFetchHeaders(),
    }
  );
  if (!res.ok) {
    throw new Error("Failed to load invite details");
  }
  return await jsonFromResponse<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>(res, "Failed to load invite details");
}

export async function getInviteMeta(token: string): Promise<{
  inviteeEmail?: string | null;
  targetType: string;
  status: string;
  expiresAt: string;
  organizationId: string;
  organizationName?: string | null;
  locationId?: string | null;
  locationName?: string | null;
  role?: string | null;
  userExists: boolean;
  hasPassword: boolean;
  firstName?: string;
  lastName?: string;
}> {
  const res = await fetch(
    `${url}/invite/meta?token=${encodeURIComponent(token)}`,
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    }
  );
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to load invite")
    );
  }
  const data = await jsonFromResponse<{
    inviteeEmail?: string;
    invitee_email?: string;
    targetType?: string;
    target_type?: string;
    status?: string;
    expiresAt?: string;
    expires_at?: string;
    organizationId?: string;
    organization_id?: string;
    organizationName?: string | null;
    organization_name?: string | null;
    locationId?: string | null;
    location_id?: string | null;
    locationName?: string | null;
    location_name?: string | null;
    role?: string | null;
    userExists?: boolean;
    user_exists?: boolean;
    hasPassword?: boolean;
    has_password?: boolean;
    firstName?: string;
    first_name?: string;
    lastName?: string;
    last_name?: string;
  }>(res, "Failed to load invite");
  return {
    inviteeEmail: data.inviteeEmail ?? data.invitee_email ?? null,
    targetType: data.targetType ?? data.target_type ?? "",
    status: normalizeInviteStatus(data.status ?? ""),
    expiresAt: data.expiresAt ?? data.expires_at ?? "",
    organizationId: data.organizationId ?? data.organization_id ?? "",
    organizationName: data.organizationName ?? data.organization_name ?? null,
    locationId: data.locationId ?? data.location_id ?? null,
    locationName: data.locationName ?? data.location_name ?? null,
    role: data.role ?? null,
    userExists: data.userExists ?? data.user_exists ?? false,
    hasPassword: data.hasPassword ?? data.has_password ?? false,
    firstName: data.firstName ?? data.first_name,
    lastName: data.lastName ?? data.last_name,
  };
}

export async function acceptInvite(params: {
  token: string;
  email?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  password: string;
}): Promise<{
  organizationId?: string | null;
  locationId?: string | null;
  status?: string;
  targetType?: string;
}> {
  const res = await fetch(`${url}/invite/accept`, {
    method: "POST",
    credentials: "include",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to accept invite", {
        humanizeAuth: true,
      })
    );
  }
  const data = await jsonFromResponse<{
    organizationId?: string | null;
    organization_id?: string | null;
    locationId?: string | null;
    location_id?: string | null;
    status?: string;
    targetType?: string;
    target_type?: string;
  }>(res, "Failed to accept invite");
  return {
    organizationId: data.organizationId ?? data.organization_id ?? null,
    locationId: data.locationId ?? data.location_id ?? null,
    status: data.status,
    targetType: data.targetType ?? data.target_type,
  };
}

export async function updateUserProfile(payload: {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  sidebarRestoreLastVisited?: boolean;
  sidebarShowRememberedShortcuts?: boolean;
}): Promise<void> {
  const res = await fetch(`${url}/edit`, {
    method: "POST",
    credentials: "include",
    headers : await defaultFetchHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to update profile")
    );
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  const res = await fetch(`${url}/password/reset/request`, {
    method: "POST",
    credentials: "include",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({ email: normalizeIdentityEmail(email) }),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to request password reset", {
        humanizeAuth: true,
      })
    );
  }
}

export async function confirmPasswordReset(params: {
  token: string;
  password: string;
}): Promise<{ email: string }> {
  const res = await fetch(`${url}/password/reset/confirm`, {
    method: "POST",
    credentials: "include",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to reset password", {
        humanizeAuth: true,
      })
    );
  }
  return res.json();
}

export async function changePassword(params: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  const res = await fetch(`${url}/password/change`, {
    method: "POST",
    credentials: "include",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to change password", {
        humanizeAuth: true,
      })
    );
  }
}

export async function verifyCurrentPassword(
  currentPassword: string
): Promise<void> {
  const res = await fetch(`${url}/password/verify`, {
    method: "POST",
    credentials: "include",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({ currentPassword }),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to verify password", {
        humanizeAuth: true,
      })
    );
  }
}

export type SecurityChallengeAction = "change_email";

export async function requestSecurityChallenge(params: {
  action: SecurityChallengeAction;
  currentPassword?: string;
  newEmail?: string;
  challengeId?: string;
}): Promise<{
  challengeId: string;
  destinationHint: string;
  resent?: boolean;
}> {
  const payload = {
    ...params,
    newEmail: params.newEmail
      ? normalizeIdentityEmail(params.newEmail)
      : undefined,
  };
  const res = await fetch(`${url}/security/challenge/request`, {
    method: "POST",
    credentials: "include",
    headers : await defaultFetchHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to request security code", {
        humanizeAuth: true,
      })
    );
  }
  const result = await jsonFromResponse<{
    challengeId?: string;
    destinationHint?: string;
    resent?: boolean;
    challenge_id?: string;
    destination_hint?: string;
  }>(res, "Failed to request security code");
  return {
    challengeId: result.challengeId ?? result.challenge_id ?? "",
    destinationHint: result.destinationHint ?? result.destination_hint ?? "",
    resent: result.resent,
  };
}

export async function confirmSecurityChallenge(params: {
  challengeId: string;
  code: string;
}): Promise<{ status: string; email?: string }> {
  const res = await fetch(`${url}/security/challenge/confirm`, {
    method: "POST",
    credentials: "include",
    headers : await defaultFetchHeaders(),
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to confirm security code", {
        humanizeAuth: true,
      })
    );
  }
  return await jsonFromResponse<{ status: string; email?: string }>(
    res,
    "Failed to confirm security code"
  );
}


export async function updateUserPhoto(file: File | null, userId?: string): Promise<userType> {
  const form = new FormData();
  if (file) form.append("file", file);
  const token = getCsrfToken() || await setCsrfToken();
  const user = await fetchJson<userType>(`${url}/photo${file ? "" : "/remove"}${userId ? `?userId=${encodeURIComponent(userId)}` : ""}`, {
    method: "POST", credentials: "include", headers: { ...(await defaultFetchHeaders(null)), "X-CSRFToken": token },
    ...(file ? { body: form } : {}),
  }, "Unable to update profile photo");
  return normalizeUserResponse(user);
}
